import React, { Suspense, useState } from 'react';
import { lazyRoute } from '@/utils/lazyRoute';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LogIn, User, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import type { AuthView } from './AuthPopover';
import { cn } from '@/lib/utils';

const LazyAuthSignIn = lazyRoute(() => import('./AuthSignIn'));
const LazyAuthSignUp = lazyRoute(() => import('./AuthSignUp'));
const LazyAuthResetPassword = lazyRoute(() => import('./AuthResetPassword'));

function AuthFormFallback() {
  return (
    <div className="p-6 text-sm text-muted-foreground" role="status" aria-live="polite">
      Loading account form…
    </div>
  );
}

interface AuthDialogProps {
  triggerClassName?: string;
  triggerText?: string;
  initialView?: AuthView;
}

const AuthDialog: React.FC<AuthDialogProps> = ({ 
  triggerClassName, 
  triggerText = 'Sign In',
  initialView = 'signIn'
}) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<AuthView>(initialView);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleClose = () => {
    setOpen(false);
    // Reset to initial view when closing
    setTimeout(() => setView(initialView), 300);
  };

  const handleViewChange = (newView: AuthView) => {
    setView(newView);
  };

  const handleSignOut = async () => {
    await logout();
    handleClose();
    navigate('/');
  };

  const handleProfileClick = () => {
    handleClose();
    navigate('/profile');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {user ? (
          <Button className={triggerClassName || "rounded-full px-5"} variant="outline">
            <User size={18} className="mr-2" />
            Profile
          </Button>
        ) : (
          <Button className={triggerClassName || "rounded-full px-5"}>
            <LogIn size={18} className="mr-2" />
            {triggerText}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent 
        className={cn(
          "fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] p-0 outline-none",
          // Keep a gutter on narrow screens instead of running edge to edge.
          user ? "w-[calc(100%-2rem)] max-w-xs" : "w-[calc(100%-2rem)] max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto"
        )}
      >
        <DialogTitle className="sr-only">
          {user
            ? 'Profile menu'
            : view === 'signIn'
              ? 'Sign In'
              : view === 'signUp'
                ? 'Sign Up'
                : 'Reset Password'}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {user
            ? 'Manage your profile or sign out.'
            : 'Sign in, create an account, or reset your password.'}
        </DialogDescription>
        {user ? (
          <div className="p-6 bg-card rounded-lg border shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4" />
                <span className="font-medium">{user.user_metadata?.full_name || user.email}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleClose}
              >
                <X size={16} />
              </Button>
            </div>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                onClick={handleProfileClick}
              >
                My Profile
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                onClick={handleSignOut}
              >
                Sign Out
              </Button>
            </div>
          </div>
        ) : (
          // The forms are shared with the full-page /auth screen and carry no
          // padding of their own, so the dialog supplies it.
          <div className="bg-card rounded-lg px-5 pb-6 sm:px-6">
            <div className="-mx-3 flex justify-end pt-2 sm:-mx-4">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleClose}
              >
                <X size={16} />
              </Button>
            </div>
            <Suspense fallback={<AuthFormFallback />}>
              {view === 'signIn' && (
                <LazyAuthSignIn
                  onViewChange={handleViewChange}
                  onSuccess={() => {
                    handleClose();
                    navigate('/');
                  }}
                />
              )}
              {view === 'signUp' && (
                <LazyAuthSignUp
                  onViewChange={handleViewChange}
                  onSuccess={() => {
                    handleClose();
                    navigate('/');
                  }}
                />
              )}
              {view === 'resetPassword' && (
                <LazyAuthResetPassword
                  onViewChange={handleViewChange}
                  onSuccess={() => {
                    handleClose();
                  }}
                />
              )}
            </Suspense>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
